<template>
    <div class="[ flex flex-col gap-4 ]" v-bind="$attrs">
        <div class="[ flex justify-between items-center gap-4 ]">
            <div class="[ flex-1 flex ]">
                <h2 class="[ font-bold ]">External Services</h2>
            </div>
            <ButtonComponent label="Create external service" outlined @click="openBlankDrawer">
                <template #icon>
                    <JBIcon name="add" />
                </template>
            </ButtonComponent>
        </div>

        <DataTable :value="data" :loading="loading" @row-select="selectRow($event.data)" selection-mode="single">
            <template #empty>
                <Empty icon="unavailable" title="No external service"
                    message="Create an external service to connect to your apps." />
            </template>
            <Column field="id" header="Id">
                <template #body="{ data }: { data: ExternalServiceConfig }">
                    <div>{{ data.id }}</div>
                </template>
            </Column>
            <Column field="type" header="Type">
                <template #body="{ data }: { data: ExternalServiceConfig }">
                    <div>{{ data.type }}</div>
                </template>
            </Column>
            <Column field="name" header="Name">
                <template #body="{ data }: { data: ExternalServiceConfig }">
                    <div>{{ data.name }}</div>
                </template>
            </Column>
            <Column field="description" header="Description">
                <template #body="{ data }: { data: ExternalServiceConfig }">
                    <div>{{ data.description }}</div>
                </template>
            </Column>
            <Column>
                <template #body="{ data }: { data: ExternalServiceConfig }">
                    <ButtonComponent outlined label="Delete" @click="confirmDelete(data.id!)" />
                </template>
            </Column>
        </DataTable>
    </div>
    <JBDialog v-model:visible="showConfirm" header="Delete External Service">
        <div>Are you sure you want to delete this external service?</div>
        <template #footer>
            <div class="[ flex gap-3 justify-end ]">
                <ButtonComponent label="Delete" @click="commitDelete" :loading="isDeleting" />
            </div>
        </template>
    </JBDialog>
    <JBDrawer v-model="showDrawer"
        :title="selectedId === -1 ? 'Create External Service' : `Edit External Service '${name}'`">
        <div class="[ flex flex-col h-full gap-4 p-x-3 ]">
            <GridForm size="full">
                <GridFormField id="name" label="Name">
                    <template #input="{ id }">
                        <InputText :id="id" v-model="name" placeholder="External service name" />
                    </template>
                </GridFormField>
                <GridFormField id="description" label="Description">
                    <template #input="{ id }">
                        <Textarea :id="id" v-model="description" rows="3" placeholder="External service description" />
                    </template>
                </GridFormField>
                <GridFormField id="description" label="Type">
                    <template #input="{ id }">
                        <Select :id="id" v-model="type" :options="availableTypes" option-label="label"
                            option-value="value" placeholder="Select a type" />
                    </template>
                </GridFormField>
                <component :is="optionsComponent" v-model="options" />
            </GridForm>

            <div class="[ flex justify-end ]">
                <ButtonComponent :label="selectedId === -1 ? 'Create' : 'Update'" :loading="isUpdating || isCreating"
                    @click="commit" :disabled="!validForm" />
            </div>
        </div>
    </JBDrawer>
</template>

<script setup lang="ts">
import { Column, DataTable, Button as ButtonComponent, InputText, Select, Textarea } from "primevue";
import { useApi, useApiLoader } from '@/services/api';
import { ExternalServiceAwsLambdaConfigFromJSON, ExternalServiceConfig, ExternalServiceHttpConfigFromJSON, ExternalServiceType } from '@/services/api/sdk';
import { computed, onMounted, ref } from 'vue';
import JBDrawer from "@/components/ui/modals/JBDrawer.vue";
import GridForm from "@/components/ui/GridForm.vue";
import GridFormField from "@/components/ui/GridFormField.vue";
import JBDialog from "@/components/ui/modals/JBDialog.vue";
import Empty from "@/components/ui/Empty.vue";
import ExternalServiceWebOptions from "@/components/integrations/ExternalServiceWebOptions.vue";
import ExternalServiceAWSLambdaOptions from "@/components/integrations/ExternalServiceAWSLambdaOptions.vue";
import { useExternalServices } from "@/composables/external-services";

defineOptions({
    inheritAttrs: false,
});

definePage({
    meta: { title: 'External Services', icon: 'link-square' },
});

// REFS

const showDrawer = ref(false);
const showConfirm = ref(false);
const selectedId = ref(-1);
const name = ref('');
const description = ref('');
const type = ref<ExternalServiceType | null>(null);
const options = ref<any>({});

const availableTypes = [
    { label: 'Http', value: ExternalServiceType.Web },
    { label: 'AWS Lambda', value: ExternalServiceType.AwsLambda },
];

// COMPUTED 

const validForm = computed(() => {
    return name.value;
});

const optionsComponent = computed(() => {
    switch (type.value) {
        case ExternalServiceType.Web:
            return ExternalServiceWebOptions;
        case ExternalServiceType.AwsLambda:
            return ExternalServiceAWSLambdaOptions;
        default: return null;
    }
});

// API

const { invalidate } = useExternalServices();

const api = useApi();

const { data, load, loading } = useApiLoader(async () => {
    const res = await api.getExternalServices();
    return res.data!;
}, []);

const { loading: isUpdating, load: updateService } = useApiLoader(async () => {
    await api.updateExternalService({
        serviceId: selectedId.value,
        updateExternalServiceRequest: {
            name: name.value,
            description: description.value,
            type: type.value!,
            options: options.value,
        },
    });
}, null);

const { loading: isCreating, load: createService } = useApiLoader(async () => {
    await api.createExternalService({
        createExternalServiceRequest: {
            name: name.value,
            description: description.value,
            type: type.value!,
            options: options.value,
        },
    });
}, null);

const { loading: isDeleting, load: deleteService } = useApiLoader(async () => {
    await api.deleteExternalService({
        serviceId: selectedId.value,
    });
}, null);

// FUNCTION

function selectRow(data: ExternalServiceConfig) {
    showDrawer.value = true;
    name.value = data.name || '';
    description.value = data.description || '';
    selectedId.value = data.id!;
    type.value = data.type || null;
    const parse = data.type === ExternalServiceType.Web ? ExternalServiceHttpConfigFromJSON : ExternalServiceAwsLambdaConfigFromJSON;
    options.value = parse(data.options || {});
}

function openBlankDrawer() {
    showDrawer.value = true;
    name.value = '';
    description.value = '';
    selectedId.value = -1;
    options.value = {};
}

function confirmDelete(id: number) {
    selectedId.value = id;
    showConfirm.value = true;
}

async function commit() {
    const action = selectedId.value === -1 ? createService : updateService;
    if (await action()) {
        showDrawer.value = false;
        await load();
    }
    invalidate();
}

async function commitDelete() {
    await deleteService();
    showConfirm.value = false;
    await load();
}

onMounted(async () => {
    load();
});

</script>